import NepaliDate from 'nepali-date';

// ─── BS month name arrays ─────────────────────────────────────────────────────

export const BS_MONTHS_FULL = [
  'Baisakh', 'Jestha', 'Ashadh', 'Shrawan', 'Bhadra', 'Ashwin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
] as const;

export const BS_MONTHS_SHORT = [
  'Bai', 'Jes', 'Ash', 'Shr', 'Bha', 'Asw',
  'Kar', 'Man', 'Pou', 'Mag', 'Fal', 'Cha',
] as const;

const DAY_ABBRS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const AD_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Safe NepaliDate constructor (returns null for out-of-range dates) ────────

function safeND(date: Date): NepaliDate | null {
  if (isNaN(date.getTime())) return null;
  try {
    // Nepal changed timezone from UTC+5:30 (pre-1986) to UTC+5:45 (post-1986).
    // The library's EPOCH was constructed with the historical offset, so local
    // midnight creates a 15-minute gap that floors day counts down by 1.
    // Using local noon keeps us safely within the correct calendar day.
    const noon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    return new NepaliDate(noon);
  } catch {
    return null;
  }
}

// ─── Conversions ──────────────────────────────────────────────────────────────

/** Convert JS Date (AD) → NepaliDate (BS). Returns null if out of library range. */
export function adToBS(date: Date): NepaliDate {
  const nd = safeND(date);
  if (!nd) throw new Error(`Date ${date} is outside Nepali calendar supported range`);
  return nd;
}

/** Convert BS year / monthIndex (0=Baisakh) / day → JS Date (AD) */
export function bsToAD(bsYear: number, bsMonthIdx: number, bsDay: number): Date {
  return new NepaliDate(bsYear, bsMonthIdx, bsDay).getEnglishDate();
}

/** Parse an AD date string ('yyyy-MM-dd' or ISO timestamp) and return its NepaliDate. Returns null if out of range. */
export function adStrToBS(adStr: string): NepaliDate {
  const dateOnly = adStr.slice(0, 10);
  const date = new Date(dateOnly + 'T00:00:00');
  const nd = safeND(date);
  if (!nd) throw new Error(`Date string "${adStr}" is outside Nepali calendar supported range`);
  return nd;
}

/** Same as adStrToBS but returns null instead of throwing for out-of-range dates. */
export function adStrToBSSafe(adStr: string): NepaliDate | null {
  try {
    const dateOnly = adStr.slice(0, 10);
    return safeND(new Date(dateOnly + 'T00:00:00'));
  } catch {
    return null;
  }
}

// ─── Current BS date helpers ──────────────────────────────────────────────────

export function getCurrentBSYear(): number  { return new NepaliDate().getYear();  }
export function getCurrentBSMonth(): number { return new NepaliDate().getMonth(); } // 0-indexed
export function getCurrentBSDay(): number   { return new NepaliDate().getDate();  }

// ─── Month / year range helpers ───────────────────────────────────────────────

/** Number of days in a given BS month (bsMonthIdx 0=Baisakh) */
export function bsDaysInMonth(bsYear: number, bsMonthIdx: number): number {
  const firstOfNext = new NepaliDate(bsYear, bsMonthIdx + 1, 1).getEnglishDate();
  const last = new Date(firstOfNext);
  last.setDate(last.getDate() - 1);
  return new NepaliDate(last).getDate();
}

/** AD date range string { from, to } covering a full BS month */
export function bsMonthRangeAD(bsYear: number, bsMonthIdx: number): { from: string; to: string } {
  const fromDate = bsToAD(bsYear, bsMonthIdx, 1);
  const days     = bsDaysInMonth(bsYear, bsMonthIdx);
  const toDate   = bsToAD(bsYear, bsMonthIdx, days);
  return { from: fmtAD(fromDate), to: fmtAD(toDate) };
}

/** AD date range string { from, to } covering a full BS year (Baisakh 1 → Chaitra last) */
export function bsYearRangeAD(bsYear: number): { from: string; to: string } {
  const fromDate = bsToAD(bsYear, 0, 1);
  const lastDays = bsDaysInMonth(bsYear, 11);
  const toDate   = bsToAD(bsYear, 11, lastDays);
  return { from: fmtAD(fromDate), to: fmtAD(toDate) };
}

// ─── Formatting ───────────────────────────────────────────────────────────────

/** Format JS Date as 'yyyy-MM-dd' (AD) */
export function fmtAD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today as 'yyyy-MM-dd' (AD) */
export function todayAD(): string {
  const d = new Date();
  return fmtAD(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
}

/** "Mon 17" — weekday abbreviation + BS day number (falls back to "Mon 17 Jun" if out of range) */
export function formatBSDayLabel(adDate: Date): string {
  if (isNaN(adDate.getTime())) return '';
  const nd = safeND(adDate);
  if (!nd) return `${DAY_ABBRS[adDate.getDay()]} ${adDate.getDate()} ${AD_MONTHS_SHORT[adDate.getMonth()]}`;
  return `${DAY_ABBRS[adDate.getDay()]} ${nd.getDate()}`;
}

/** "17 Bai 2081" (falls back to "17 Jun 2026" if out of range) */
export function formatBSDate(adDate: Date): string {
  const nd = safeND(adDate);
  if (!nd) return `${adDate.getDate()} ${AD_MONTHS_SHORT[adDate.getMonth()]} ${adDate.getFullYear()}`;
  return `${nd.getDate()} ${BS_MONTHS_SHORT[nd.getMonth()]} ${nd.getYear()}`;
}

/** "17 Baisakh 2081" (falls back to "17 June 2026" if out of range) */
export function formatBSDateFull(adDate: Date): string {
  const nd = safeND(adDate);
  if (!nd) return adDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}`;
}

// ─── Year option list ─────────────────────────────────────────────────────────

/** Array of BS years: [cur-count+1 … cur] */
export function getBSYearOptions(count = 10): number[] {
  const cur = getCurrentBSYear();
  return Array.from({ length: count }, (_, i) => cur - count + 1 + i);
}

// ─── Date-time formatting (BS date + wall-clock time) ─────────────────────────

/** "17 Baisakh 2081 14:30" (falls back to AD if out of range) */
export function formatBSDateTime(date: Date): string {
  const nd = safeND(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  if (!nd) return `${date.getDate()} ${AD_MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()} ${hh}:${mm}`;
  return `${nd.getDate()} ${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()} ${hh}:${mm}`;
}

/** "17 Bai 14:30" (falls back to AD if out of range) */
export function formatBSDateTimeShort(date: Date): string {
  const nd = safeND(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  if (!nd) return `${date.getDate()} ${AD_MONTHS_SHORT[date.getMonth()]} ${hh}:${mm}`;
  return `${nd.getDate()} ${BS_MONTHS_SHORT[nd.getMonth()]} ${hh}:${mm}`;
}

/** "17 Bai 14:30:05" (falls back to AD if out of range) */
export function formatBSDateTimeWithSec(date: Date): string {
  const nd = safeND(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  if (!nd) return `${date.getDate()} ${AD_MONTHS_SHORT[date.getMonth()]} ${hh}:${mm}:${ss}`;
  return `${nd.getDate()} ${BS_MONTHS_SHORT[nd.getMonth()]} ${hh}:${mm}:${ss}`;
}

/** "Baisakh 2081" — month + year only (falls back to AD if out of range) */
export function formatBSMonthYear(date: Date): string {
  const nd = safeND(date);
  if (!nd) return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return `${BS_MONTHS_FULL[nd.getMonth()]} ${nd.getYear()}`;
}
